// React
import { useState, useEffect, useRef, useContext } from 'react'

// Libraries
import { useFormikContext } from 'formik'

// Context
import { PickerContext } from 'ui/components/Picker/Provider/Picker'

// Hooks
import { useOrm } from 'shared/utils/useOrm'
import { useSettings } from 'shared/utils/useSettings.js'

// Shared
import moment from 'shared/lib/moment'
import { parseFixedDefaultTime } from 'shared/utils/time'
import { calculateStopTimeForFixedDuration, defaultDefaultTimes } from './utils'

export const usePicker = () => {
  const context = useContext(PickerContext)

  if (!context) {
    throw Error('"usePicker" should be used within "PickerProvider"')
  }

  return context
}

export const useDurations = (onChange) => {
  const clearCacheRef = useRef(null)

  const [state, setState] = useState({
    duration: undefined,
    durationObject: undefined
  })

  const form = useFormikContext()

  const durations = useOrm('operating_rules', (rules) => {
    return rules.getAll({ data_type: 'timeslot_duration' })
      .filter((rule) => rule.data.duration > 0)
  })

  const startDefaultTimeSetting = useSettings('orders.start_fixed_at', { default: defaultDefaultTimes.start })
  const stopDefaultTimeSetting = useSettings('orders.stop_fixed_at', { default: defaultDefaultTimes.stop })

  const getRange = (from, selectedValues = {}, durationObject) => {
    if (!from) { return {} }

    const durationObjectWithFallback = durationObject || state.durationObject
    const durationData = durationObjectWithFallback.data

    const momentFrom = moment(from)

    const defaultTimes = {
      start: parseFixedDefaultTime(startDefaultTimeSetting),
      stop: parseFixedDefaultTime(stopDefaultTimeSetting)
    }

    // If start time is not selected yet, set to default start time
    if (!selectedValues.starts_at) {
      momentFrom.set({ h: defaultTimes.start.hour, m: defaultTimes.start.minute })
    }

    const momentTo = calculateStopTimeForFixedDuration({
      startTime: momentFrom,
      duration: durationData.duration,
      period: durationData.period,
      defaultTimes
    })

    // If we have stops_at, reuse the time from there as it was lost when recalculating duration in calculateStopTimeForFixedDuration
    if (selectedValues.stops_at && durationObject.data.period !== 'hour') {
      const momentSelectedStopsAt = moment(selectedValues.stops_at)
      momentTo.set({ h: momentSelectedStopsAt.hours(), m: momentSelectedStopsAt.minutes() })
    }

    return {
      from: momentFrom.toDate(),
      to: momentTo.toDate()
    }
  }

  const handleChangeDuration = ({ duration, durationObject, changePeriod = false }) => {
    clearCacheRef.current?.()

    setState({
      duration,
      durationObject
    })

    if (!changePeriod) return

    const startsAt = form.values.starts_at

    if (startsAt) {
      // If the previous period was hour and the new one is not, we need to reset the stops_at.
      // This triggers the getRange function to set the stops_at to the default time.
      const previousPeriod = state.durationObject?.get('data').period
      const currentPeriod = durationObject.get('data').period
      const periodNoLongerHour = previousPeriod === 'hour' && currentPeriod !== 'hour'
      const stopsAt = periodNoLongerHour ? null : form.values.stops_at

      const range = getRange(startsAt, { starts_at: startsAt, stops_at: stopsAt }, durationObject)

      form.setValues({
        ...form.values,
        starts_at: range.from.toJSON(),
        stops_at: range.to.toJSON(),
        charge_in_seconds: parseInt(duration)
      })
    }

    onChange?.(duration, durationObject)
  }

  useEffect(() => {
    if (state.duration || !durations || durations.size() < 1) return

    let duration
    let durationObject

    if (durations.size() === 1) {
      durationObject = durations.first()
      duration = durationObject.data.duration
    } else if (form.values.starts_at && form.values.stops_at) {
      const diff = moment(form.values.stops_at).diff(moment(form.values.starts_at), 'seconds')

      durationObject = durations.toArray().reduce((acc, duration) => {
        const currRange = getRange(form.values.starts_at, form.values, duration)
        const prevRange = getRange(form.values.starts_at, form.values, acc)

        const currInSeconds = (currRange.to - currRange.from) / 1000
        const prevInSeconds = (prevRange.to - prevRange.from) / 1000

        return Math.abs(currInSeconds - diff) < Math.abs(prevInSeconds - diff) ? duration : acc
      })
      duration = durationObject.data.length
    } else {
      const shortestDuration = durations.toArray().reduce((minDuration, current) => {
        if (current.get('data').length < minDuration.get('data').length) {
          return current
        } else {
          return minDuration
        }
      }, durations.first())

      durationObject = shortestDuration
      duration = durationObject.data.duration
    }

    if (duration) {
      handleChangeDuration({ duration, durationObject })
    }
  })

  return {
    clearCacheRef,
    duration: state.duration,
    durationObject: state.durationObject,
    onChange: handleChangeDuration,
    getRange
  }
}
